import { getMapSearchUrl } from '../../../../utils/mapSearchUrl'
import type {
  OrgRegistrationVenue,
  OrgVenueAddressApi,
  OrgVenueApi,
} from '../types'

const clean = (value?: string | null): string => value?.trim() ?? ''

const regionOf = (address: OrgVenueAddressApi): string =>
  clean(address.province?.iso_3166_2) || clean(address.province?.name)

/** A finite coordinate within ±limit; blanks and junk are not one. */
const coordinate = (
  value: number | string | null | undefined,
  limit: number
): number | null => {
  const number = typeof value === 'string' ? Number(value.trim() || NaN) : value
  return typeof number === 'number' &&
    Number.isFinite(number) &&
    Math.abs(number) <= limit
    ? number
    : null
}

/** Both coordinates, unless missing or 0,0 (an address never geocoded). */
const coordinatesOf = (address: OrgVenueAddressApi): string => {
  const latitude = coordinate(address.latitude, 90)
  const longitude = coordinate(address.longitude, 180)
  if (latitude === null || longitude === null) {
    return ''
  }
  return latitude === 0 && longitude === 0 ? '' : `${latitude},${longitude}`
}

/** "3465 Broadway St, Cheektowaga, NY 14227"; '' without an address. */
const toAddressLine = (address: OrgVenueAddressApi): string => {
  const regionPostal = [regionOf(address), clean(address.postal_code)]
    .filter(Boolean)
    .join(' ')
  return [
    clean(address.street_1),
    clean(address.street_2),
    clean(address.city),
    regionPostal,
  ]
    .filter(Boolean)
    .join(', ')
}

/**
 * A registration's venue → what the card and table show: the name, the city
 * and region under it ("Cheektowaga, NY"), the full address for its tooltip,
 * and a map link — searched by name and address, else by coordinates, else by
 * name alone. The same rules as the website plugin's locations.
 */
export const toOrgRegistrationVenue = (
  raw: OrgVenueApi
): OrgRegistrationVenue => {
  const name = clean(raw.name) || clean(raw.name_full) || 'Location'
  const address = raw.address ?? {}
  const city = clean(address.city)
  const addressLine = toAddressLine(address)
  const coordinates = coordinatesOf(address)
  const query = addressLine ? `${name}, ${addressLine}` : coordinates || name
  return {
    id: raw.id,
    name,
    place: city ? [city, regionOf(address)].filter(Boolean).join(', ') : '',
    address: addressLine,
    mapUrl: getMapSearchUrl(query),
  }
}

/** A registration's `venues`, tolerating a missing list. */
export const toOrgRegistrationVenues = (
  raw: OrgVenueApi[] | null | undefined
): OrgRegistrationVenue[] =>
  Array.isArray(raw) ? raw.map(toOrgRegistrationVenue) : []
